import { useQuery, useMutation, useQueryClient } from 'react-query';
import { apiClient, endpoints } from '../api/client';
import toast from 'react-hot-toast';

export interface ScraperIssue {
  id: string;
  url: string;
  comment: string;
  state: string;
  provider_id: string | null;
  marketplace_id: string | null;
  occurrence_count?: number;
  source?: string | null;
  created_at: string;
  updated_at: string;
}

export interface IssuesListResponse {
  items: ScraperIssue[];
  total: number;
  page: number;
  page_size: number;
}

export const useIssues = (state?: string, page: number = 1, pageSize: number = 50) => {
  return useQuery<IssuesListResponse>(
    ['issues', state, page, pageSize],
    async () => {
      const params = new URLSearchParams();
      if (state && state !== 'all') {
        params.append('state', state);
      }
      params.append('page', page.toString());
      params.append('page_size', pageSize.toString());
      
      const response = await apiClient.get(`${endpoints.issues}?${params.toString()}`);
      return response.data;
    },
    {
      refetchInterval: 30000, // Refresh every 30 seconds
      staleTime: 10000,
    }
  );
};

export const useUpdateIssueState = () => {
  const queryClient = useQueryClient();
  
  return useMutation(
    async ({ id, state }: { id: string; state: string }) => {
      const response = await apiClient.patch(endpoints.issueUpdateState(id), { state });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('issues');
        toast.success('Issue state updated successfully');
      },
      onError: (error: any) => {
        const message = error.response?.data?.detail || error.message || 'Failed to update issue';
        toast.error(message);
      },
    }
  );
};

export const useDeleteIssue = () => {
  const queryClient = useQueryClient();
  
  return useMutation(
    async (id: string) => {
      await apiClient.delete(endpoints.issue(id));
      return id;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('issues');
        toast.success('Issue deleted successfully');
      },
      onError: (error: any) => {
        const message = error.response?.data?.detail || error.message || 'Failed to delete issue';
        toast.error(message);
      },
    }
  );
};
